﻿import React from "react";
import { observer } from "mobx-react";
import type { IResourcesStore } from "../../../../resources/ResourcesStore";
import type { IUploadStore } from "../../../UploadStore";
import { injectFromAppUpload } from "../../../inject";
import type { PatientToUpload } from "../../../PatientToUpload";
import { StudyInUploadComponent } from "../study/StudyInUploadComponent";
import { UploadStatus } from "../../../FileUpload";
import { RichToolTipComponent } from "../../../../shared-components/rich-tooltip/RichToolTipComponent";
import { FileUploadComponent } from "../../file/FileUploadComponent";
import { UploadButtonLogicComponent } from "../button-and-status-logic/UploadButtonLogicComponent";
import { UploadProgressComponent } from "../button-and-status-logic/status-and-progress/UploadProgressComponent";
import { DobCheckbox, DobInput, DobPhoneInput } from "@dedalusdiit/dob-components";

interface IPatientToUploadComponent {
    resourcesStore?: IResourcesStore;
    uploadStore?: IUploadStore;
    patientToUpload: PatientToUpload;
}

interface IPatientToUploadComponentState {
    detailedInfoOpen: boolean;
}

@injectFromAppUpload<IPatientToUploadComponent>(stores => ({
    uploadStore: stores.uploadStore,
    resourcesStore: stores.resourcesStore,
}))
@observer
export class PatientToUploadComponent extends React.Component<IPatientToUploadComponent, IPatientToUploadComponentState> {
    state = {
        detailedInfoOpen: false,
    };

    toggleShowFiles = (event): void => {
        this.setState(prevState => ({
            detailedInfoOpen: !prevState.detailedInfoOpen,
        }));
    };

    removePatientToUpload = (event): void => {
        event.stopPropagation();
        const { uploadStore, patientToUpload } = this.props;
        uploadStore!.removePatientToUpload(patientToUpload);
    };

    onEmailAddressChanged = (value: string): void => {
        const { patientToUpload } = this.props;
        patientToUpload.setEmailAddress(value);
    };

    onMobilePhoneChanged = (value: string): void => {
        const { patientToUpload } = this.props;
        patientToUpload.setMobilePhone(value);
    };

    componentWillUnmount() {
        const { uploadStore, patientToUpload } = this.props;
        // Only dispose if there are no files left or patient is removed.
        // This way, if a new file is being added (and the component is re-mounted), the reaction remains active.
        const doesPatientExists = uploadStore!.patientsFound.some(patient => patient.patientGuid === patientToUpload.patientGuid);
        if (patientToUpload.filesToUpload.length === 0 || !doesPatientExists) {
            patientToUpload[Symbol.dispose]();
        }
    }

    setPrivacyPolicyConsent = (checked: boolean): void => {
        const { patientToUpload } = this.props;
        patientToUpload.setPrivacyPolicyConsent(checked);
    };

    setPatientConsent = (checked: boolean): void => {
        const { patientToUpload } = this.props;
        patientToUpload.setPatientConsent(checked);
    };

    render() {
        const { resourcesStore, patientToUpload } = this.props;
        const { resources } = resourcesStore!;
        const {
            patientId,
            patientName,
            studies,
            status,
            canBeRemoved,
            filesToUpload,
            patientDobFormatted,
            emailAddress,
            emailAddressIsValid,
            mobilePhone,
            privacyPolicyConsent,
            patientConsent,
        } = patientToUpload;
        const { detailedInfoOpen } = this.state;

        return (
            <div
                className={
                    "patient-to-upload-container" +
                    (status === UploadStatus.Paused ? " paused" : "") +
                    (status === UploadStatus.Failed ? " failed" : "") +
                    (status === UploadStatus.RateLimited ? " rate-limited" : "") +
                    (status === UploadStatus.Started ? " started" : "") +
                    (status === UploadStatus.Done ? " done" : "") +
                    (this.state.detailedInfoOpen ? " open-detailed-file-information" : "")
                }
            >
                <h3 className={"patient-name"}>{patientName}</h3>

                <div className={"patient-and-studies-container"}>
                    <div className={"row"}>
                        <div className={"patient-information-container label-value-containers-holder"}>
                            <div className={"label-value-container"}>
                                <div className={"label-item"}>{resources.DateOfBirth}</div>
                                <div className={"value-item"}>{patientDobFormatted}</div>
                            </div>
                            <div className={"label-value-container"}>
                                <div className={"label-item"}>{resources.PatientID}</div>
                                <div className={"value-item"}>{patientId}</div>
                            </div>
                            <div className={"label-value-container optional-information"}>
                                <div className={"label-item"}>{resources.OptionalPatientInformation}</div>

                                <DobInput
                                    error={!emailAddressIsValid}
                                    value={emailAddress}
                                    onValueChange={this.onEmailAddressChanged}
                                    className={"patient-email"}
                                    disabled={!canBeRemoved}
                                    placeholder={resources.EmailAddress}
                                    icon={"envelope-fill"}
                                />
                                <DobPhoneInput
                                    value={mobilePhone}
                                    disabled={!canBeRemoved}
                                    onChange={this.onMobilePhoneChanged}
                                    className={"patient-mobile-phone"}
                                    placeholder={resources.MobilePhone}
                                />
                            </div>
                        </div>
                        <div className={"studies-information-container"}>
                            {studies.map((s, i) => (
                                <StudyInUploadComponent studyInUpload={s} patientToUpload={patientToUpload} canBeRemoved={canBeRemoved} key={s.studyGuid} />
                            ))}
                        </div>
                    </div>
                    <div className={"privacy-checkbox-container"}>
                        <DobCheckbox
                            checked={privacyPolicyConsent}
                            disabled={!canBeRemoved}
                            onCheckedChange={this.setPrivacyPolicyConsent}
                            label={resources.PrivacyPolicyConsent.replace("<a>", '<a href="/POW4/Legal" target=_blank>')}
                        />
                        <DobCheckbox checked={patientConsent} disabled={!canBeRemoved} onCheckedChange={this.setPatientConsent} label={resources.PatientConsent} />
                    </div>
                </div>

                <div className={"button-and-status-container"}>
                    <UploadButtonLogicComponent patientToUpload={patientToUpload}>
                        <UploadProgressComponent patientToUpload={patientToUpload} />
                    </UploadButtonLogicComponent>
                    <p className={"small-text toggle-detailed-file-information"} onClick={this.toggleShowFiles}>
                        {detailedInfoOpen ? resources.HideDetailedFileInformation : resources.ShowDetailedFileInformation}
                    </p>
                </div>

                {this.state.detailedInfoOpen && (
                    <div className={"patient-files-container"}>
                        {filesToUpload.map(f => (
                            <FileUploadComponent key={f.fileGuid} file={f} />
                        ))}
                    </div>
                )}

                {canBeRemoved && (
                    <RichToolTipComponent toolTipResource={"RemovePatient"} onClick={this.removePatientToUpload} className={"remove-patient"}>
                        &nbsp;
                    </RichToolTipComponent>
                )}
            </div>
        );
    }
}
